import { Content } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import {
  ExpertiseSliceDefault,
  ExpertiseSliceWithTechnologies,
} from '../../../prismicio-types';
import { PrismicNextImage, PrismicNextLink } from '@prismicio/next';

enum SLICE_VARIATION {
  default = 'default',
  withTechnologies = 'withTechnologies',
}

/**
 * Props for `Expertise`.
 */
export type ExpertiseProps = SliceComponentProps<Content.ExpertiseSlice>;

/**
 * Component for "Expertise" Slices.
 */
const Expertise = ({ slice }: ExpertiseProps): JSX.Element => {
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      {slice.variation === SLICE_VARIATION.default && defaultVariation(slice)}
      {slice.variation === SLICE_VARIATION.withTechnologies &&
        withTechnologiesVariation(slice)}
    </section>
  );
};

const defaultVariation = (slice: ExpertiseSliceDefault): JSX.Element => {
  const {
    small_title: smallTitle,
    title,
    button_label: buttonLabel,
    button_link: buttonLink,
    features,
  } = slice.primary;

  return (
    <div className="bg-mint-green-gradient-2">
      <div className="px-5 md:px-16 py-20 max-w-screen-2xl mx-auto">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex flex-col gap-y-4">
            <PrismicRichText field={smallTitle} />
            <PrismicRichText field={title} />
            <PrismicNextLink
              field={buttonLink}
              className="button-bordered-shade-green text-black"
            >
              <span>{buttonLabel}</span>
            </PrismicNextLink>
          </div>
          <div className="flex flex-col gap-y-4">
            {features.map((feature, index) => (
              <div key={`feature-${index}`} className="flex gap-x-4">
                <div className="h-[30px] w-[30px] flex-shrink-0 mt-5">
                  <PrismicNextImage field={feature.icon} />
                </div>
                <div>
                  <PrismicRichText field={feature.title} />
                  <PrismicRichText field={feature.description} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const withTechnologiesVariation = (
  slice: ExpertiseSliceWithTechnologies,
): JSX.Element => {
  const { small_title: smallTitle, title, technologies } = slice.primary;

  return (
    <div className="bg-mint-green-gradient-3">
      <div className="px-5 md:px-16 py-20 max-w-screen-2xl mx-auto">
        <PrismicRichText field={smallTitle} />

        <div className="flex flex-col lg:flex-row gap-x-20 gap-y-10 mt-6">
          <div className="w-full lg:w-1/2">
            <PrismicRichText field={title} />
          </div>
          <div className="w-full lg:w-1/2 flex gap-x-4 justify-between">
            {technologies.map(({ technology }, index) => (
              <div key={`technology-${index}`}>
                <div className="">
                  <PrismicNextImage field={technology} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
export default Expertise;
